<template>
    <Card :title="$t('labels.status_matrix')">
        <div class="card-body">
            <InputGroup class="mb-3">
                <template #prepend>
                    <AppButton size="sm"
                        :disabled="!importStatusServices.includes(integration.service) || !integration.api_credentials?.is_active"
                        color="light"
                        @click="fetchOrdersStatuses"
                    >
                        {{ $t('buttons.update_orders_statuses') }}
                    </AppButton>
                </template>
                <span v-if="!importStatusServices.includes(integration.service)" class="mr-2">
                    {{ `${$t('alerts.not_available_in_this_integration')}.` }}
                </span>
                <span v-if="!integration.api_credentials?.is_active">
                    {{ `${$t('alerts.enable_credentials')}.` }}
                </span>
            </InputGroup>
            <div class="table-responsive">
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th style="width: 20%;">
                                {{ $t('labels.order_statuses') }}
                            </th>
                            <th style="width: 50%;">
                                {{ $t('labels.external_statuses') }}
                            </th>
                            <th style="width: 15%;">
                                {{ $t('labels.main_for_webhook') }}
                            </th>
                            <th style="width: 15%;">
                                {{ $t('labels.create_on_import') }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <slot v-for="orderStatus in orderStatuses">
                            <slot v-if="connectedStatuses[orderStatus.uuid] !== undefined">
                                <tr v-for="(connectedStatus, connectedStatusIndex) in connectedStatuses[orderStatus.uuid]" >
                                    <td :rowspan="connectedStatuses[orderStatus.uuid].length + 1" v-if="connectedStatusIndex === 0">
                                        {{ orderStatus.name }}
                                    </td>
                                    <td>
                                        <InputGroup>
                                            <Input :disabled="true" :model-value="integrationStatuses[connectedStatus.integration_order_status_uuid]?.value"/>
                                            <template #append>
                                                <AppButton size="sm"
                                                    @click="removeConnectedStatus(orderStatus, connectedStatusIndex)"
                                                    color="danger"
                                                >
                                                    {{ $t('buttons.delete') }}
                                                </AppButton>
                                            </template>
                                        </InputGroup>
                                    </td>
                                    <td>
                                        <Radio :model-value="Boolean(connectedStatuses[orderStatus.uuid][connectedStatusIndex].main_for_webhook)" @update:model-value="selectMainForExport(orderStatus, connectedStatusIndex)"/>
                                    </td>
                                    <td>
                                        <AppCheckbox v-model="connectedStatuses[orderStatus.uuid][connectedStatusIndex].allow_creation_on_import"/>
                                    </td>
                                </tr>
                            </slot>
                            <tr>
                                <td v-if="connectedStatuses[orderStatus.uuid] === undefined">
                                    {{ orderStatus.name }}
                                </td>
                                <td>
                                    <AppAutoComplete
                                        :url="`/integrations/${integration.uuid}/order-statuses`"
                                        option-value="value"
                                        @update:modelValue="externalStatus => addConnectedStatus(orderStatus, externalStatus)"
                                        :selected-values="Object.values(connectedStatuses).flat().map(item => item.integration_order_status_uuid)"
                                    />
                                </td>
                                <td></td>
                                <td></td>
                            </tr>
                        </slot>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end">
            <AppButton @click="save" color="indigo">{{ $t('buttons.save') }}</AppButton>
        </div>
    </Card>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import InputGroup from "../../../../components/new/form/InputGroup.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import Input from "../../../../components/new/form/Input.vue";
import AppAutoComplete from "../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import Radio from "../../../../components/new/form/radio/Radio.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";

import { httpClient } from "../../../../services/HttpClient";
import { IntegrationServiceEnum } from "../../../enums/IntegrationServiceEnum";
import MessageNotificator from "../../../../services/MessageNotificator";

export default {
    name: "IntegrationStatusMatrix",
    components: {
        Card, InputGroup, AppButton, Input, AppAutoComplete, Radio, AppCheckbox
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            orderStatuses: [],
            integrationStatuses: {},
            importStatusServices: [
                IntegrationServiceEnum.PROM,
                IntegrationServiceEnum.ROZETKA,
                IntegrationServiceEnum.HOROSHOP,
                IntegrationServiceEnum.SHOP_EXPRESS
            ],
            connectedStatuses: {}
        }
    },
    methods: {
        async fetchOrderStatuses() {
            let totalStatusCount = 0;

            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/statuses`, {
                limit: 1
            }).then(response => {
                totalStatusCount = response.data.meta.total;
            })

            for (let i = 0; i < totalStatusCount; i += 10) {
                await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/statuses`, {
                    limit: 10,
                    page: Math.floor(i / 10) + 1,
                    orders: [
                        {
                            column: 'priority_order',
                            dir: 'asc'
                        }
                    ]
                }).then(response => {
                    this.orderStatuses.push(...response.data.data);
                })
            }
        },
        async fetchIntegrationStatuses() {
            let totalStatusCount = 0;

            await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/order-statuses`, {
                limit: 1
            }).then(response => {
                totalStatusCount = response.data.meta.total;
            })

            for (let i = 0; i < totalStatusCount; i += 10) {
                await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/order-statuses`, {
                    limit: 10,
                    page: Math.floor(i / 10) + 1
                }).then(response => {
                    for (let integrationStatus of response.data.data) {
                        this.integrationStatuses[integrationStatus.uuid] = integrationStatus;
                    }
                })
            }
        },
        fetchOrdersStatuses() {
            httpClient.post(`/integrations/${this.integration.uuid}/orders/statuses/fetch`).then(() => {
                this.fetchIntegrationStatuses();
                new MessageNotificator().success(this.$t('alerts.success'))
            })
        },
        async fetchConnectedStatuses() {
            await httpClient.get(`/integrations/${this.integration.uuid}/order-status-relationships`).then(response => {
                let connectedStatus = response.data.data.reduce((acc, item) => {
                    if (acc[item.order_status_uuid]) {
                        acc[item.order_status_uuid].push(item);
                    } else {
                        acc[item.order_status_uuid] = [item];
                    }

                    return acc;
                }, {});

                this.connectedStatuses = {...this.connectedStatuses, ...connectedStatus};
            });
        },
        removeConnectedStatus(orderStatus, connectedStatusIndex) {
            if (this.connectedStatuses[orderStatus.uuid].length === 1) {
                delete this.connectedStatuses[orderStatus.uuid];
                return;
            }

            if (this.connectedStatuses[orderStatus.uuid][connectedStatusIndex].main_for_webhook === true) {
                this.connectedStatuses[orderStatus.uuid].splice(connectedStatusIndex, 1);
                this.connectedStatuses[orderStatus.uuid][0].main_for_webhook = true;
                return;
            }

            this.connectedStatuses[orderStatus.uuid].splice(connectedStatusIndex, 1);
        },
        addConnectedStatus(orderStatus, integrationStatus) {
            const connectedStatus = {
                order_status_uuid: orderStatus.uuid,
                integration_order_status_uuid: integrationStatus.uuid,
                integration_uuid: this.integration.uuid,
                main_for_webhook: this.connectedStatuses[orderStatus.uuid] ? false : true,
                allow_creation_on_import: false
            };

            if (this.connectedStatuses[orderStatus.uuid]) {
                this.connectedStatuses[orderStatus.uuid].push(connectedStatus);
            } else {
                this.connectedStatuses[orderStatus.uuid] = [connectedStatus];
            }
        },
        async save() {
            let request = {};

            if (Object.keys(this.connectedStatuses).length > 0) {
                let connectedStatuses = Object.values(this.connectedStatuses).flat();

                request = connectedStatuses.map(item => ({
                    order_status_uuid: item.order_status_uuid,
                    integration_order_status_uuid: item.integration_order_status_uuid,
                    main_for_webhook: item.main_for_webhook ?? false,
                    allow_creation_on_import: item.allow_creation_on_import ?? false
                }))
            }

            await httpClient.put(`/integrations/${this.integration.uuid}/order-status-relationships`, {
                data: request
            }).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'));
            });
        },
        selectMainForExport(orderStatus, connectedStatusIndex) {
            this.connectedStatuses[orderStatus.uuid].forEach((item, index) => {
                item.main_for_webhook = index === connectedStatusIndex ? true : false;
            });
        },
    },
    mounted() {
        this.fetchOrderStatuses();
        this.fetchIntegrationStatuses();
        this.fetchConnectedStatuses();
    }
}
</script>